import { forwardRef } from "react";
import { formatUSPhone } from "../lib/phone";

/**
 * USPhoneInput — text input that auto-masks US phone numbers as the user
 * types. The displayed value is always pretty-formatted ("(336) 555-1234"),
 * but the value emitted to the parent's `onChange` handler is JUST DIGITS so
 * the underlying form state stays normalized for the backend.
 *
 * Why a digits-only emit?
 * - Backend stores raw digits / E.164 already (Twilio, sms.normalize_e164,
 *   tenant.phone). Storing display-formatted values would force everything
 *   downstream to re-strip on every read.
 * - This way every existing API contract continues to work unchanged.
 *
 * Drop-in replacement for `<input type="tel">`:
 *
 *   <USPhoneInput
 *     value={form.phone}                    // raw digits
 *     onChange={(digits) => setForm({...form, phone: digits})}
 *     data-testid="tenant-phone-input"
 *     placeholder="(336) 555-1234"
 *     className="input-clean w-full"
 *   />
 */
const USPhoneInput = forwardRef(function USPhoneInput(
  { value, onChange, className = "input-clean w-full", placeholder = "(555) 555-1234", ...rest },
  ref
) {
  const handleChange = (e) => {
    const raw = e.target.value || "";
    // Allow `+` for E.164-prefixed input but otherwise strip to digits
    const digits = raw.startsWith("+") && raw.length > 1
      ? "+" + raw.slice(1).replace(/\D/g, "")
      : raw.replace(/\D/g, "");
    // Cap at 11 digits (US country-code + NANP) to avoid runaway input
    const trimmed = digits.startsWith("+")
      ? "+" + digits.slice(1).slice(0, 11)
      : digits.slice(0, 11);
    onChange?.(trimmed);
  };

  // For E.164 values starting with `+`, show them as-is (international); for
  // plain digits, format as US NANP. Empty string → empty string.
  const displayValue = !value
    ? ""
    : String(value).startsWith("+") && !String(value).startsWith("+1")
      ? String(value)
      : formatUSPhone(value);

  return (
    <input
      ref={ref}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      maxLength={20}
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
      {...rest}
    />
  );
});

export default USPhoneInput;
